import { Heart, History } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useCatalog } from "../contexts/CatalogContext";
import type { Stream } from "../lib/types";
import { loadNowPlaying, type NowPlaying } from "../lib/nowPlaying";
import { useFavorite } from "../lib/useFavorite";
import { PosterImage } from "./PosterImage";

/**
 * Phone/tablet Live TV row.
 *
 * A logo tile cannot carry an IPTV channel name — "UK: SKY SPORTS MAIN EVENT
 * FHD" needs two lines, and the now-playing programme needs a third. So on
 * small screens the channel grid becomes a list of rows.
 *
 * EPG fetching is the load-bearing part. There are commonly thousands of
 * channels in a playlist, and one now-playing lookup per channel would be a
 * lot of upstream traffic if it were done eagerly. Three things keep it safe:
 *
 *  1. The backend serves `/epg/short/` cache-first (see `routers/epg.py`) —
 *     a hit never reaches the panel at all.
 *  2. We only fetch for rows that are actually on screen, via
 *     IntersectionObserver. Scrolling past a row fast enough never fetches it.
 *  3. A module-level cache dedupes across rows, re-renders, and revisits for
 *     the life of the tab, so a channel is fetched at most once per session
 *     even if you scroll past it repeatedly.
 *
 * The row reserves the programme line's height whether or not EPG has arrived,
 * so nothing reflows when it does.
 */

export function ChannelRow({ stream, href }: { stream: Stream; href: string }) {
  const { activePlaylist } = useCatalog();
  const ref = useRef<HTMLDivElement>(null);
  const [epg, setEpg] = useState<NowPlaying>(null);
  const { isFavorite, toggle: toggleFavorite } = useFavorite(stream);

  const pid = activePlaylist?.id;
  const isXtream = activePlaylist?.kind === "xtream";
  const hasEpgId = !!stream.epg_channel_id;

  useEffect(() => {
    // No EPG id means the panel has nothing to look up — don't spend a request
    // discovering that. This is a large share of channels on a real playlist.
    if (!ref.current || !pid || !isXtream || !hasEpgId) return;
    const el = ref.current;
    let cancelled = false;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        loadNowPlaying(pid, Number(stream.id)).then((v) => {
          if (!cancelled) setEpg(v);
        });
      },
      { rootMargin: "200px 0px" },
    );
    io.observe(el);
    return () => {
      cancelled = true;
      io.disconnect();
    };
  }, [pid, isXtream, hasEpgId, stream.id]);

  const mins = epg ? Math.round(epg.endsInS / 60) : 0;

  return (
    <div ref={ref} className="relative">
      <Link
        to={href}
        className="flex items-center gap-3 p-3 rounded-card bg-surface border border-line hover:bg-elevated transition-colors duration-press ease-bz"
      >
        <div className="w-12 h-12 flex-shrink-0 rounded-ctrl bg-elevated overflow-hidden flex items-center justify-center">
          <PosterImage
            src={stream.logo || stream.cover || undefined}
            alt={stream.name}
            className="w-full h-full"
            imgClass="w-full h-full object-contain p-1"
            orientation="row"
          />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-ui text-text-primary leading-tight line-clamp-2">
            {stream.name}
          </p>
          {/* Both lines reserve their height whether or not EPG resolves, so
              arriving data never reflows the list under the reader's thumb. */}
          <div className="mt-1 h-[16px] flex items-center gap-1.5">
            <span className="text-accent-gold font-semibold text-[10px] uppercase tracking-[0.08em]">
              Live
            </span>
            {epg && mins > 0 && (
              <span className="font-data text-[11px] text-text-muted">{mins} min left</span>
            )}
          </div>
          <div className="h-[18px] text-[13px] font-ui">
            {epg ? (
              <span className="text-text-secondary line-clamp-1">{epg.title}</span>
            ) : (
              <span className="text-text-muted">{hasEpgId ? "" : "No guide data"}</span>
            )}
          </div>
          <div className="mt-1 h-[3px] rounded-bar bg-line overflow-hidden">
            <div
              className="h-full bg-accent-gold transition-[width] duration-panel ease-bz"
              style={{ width: `${epg?.pct ?? 0}%` }}
            />
          </div>
        </div>

        {stream.tv_archive === 1 && (
          <History size={16} className="text-text-muted flex-shrink-0" aria-label="Catch-up available" />
        )}
        {/* Space for the favourite button, which sits outside the Link so a tap
            on it does not navigate. */}
        <span className="w-11 flex-shrink-0" aria-hidden="true" />
      </Link>

      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void toggleFavorite();
        }}
        className="absolute right-1 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full text-text-muted hover:text-accent-gold transition-colors duration-press ease-bz"
        aria-label={isFavorite ? "Remove from favourites" : "Add to favourites"}
        aria-pressed={isFavorite}
      >
        <Heart
          size={18}
          fill={isFavorite ? "currentColor" : "none"}
          className={isFavorite ? "text-accent-gold" : ""}
        />
      </button>
    </div>
  );
}
